import { useId } from 'react';

import { NumberField } from '@/components/ui/number-field';
import SelectMenu from '@/components/ui/select-menu';
import { Switch } from '@/components/ui/switch';

/** The three student-display modes a sensor block can be authored in. Structurally identical to
 * `CompassVisibilityMode`/`GPSVisibilityMode` in `@agavi/blocks` — declared here as a plain union so
 * this module stays usable by any sensor block without importing every block's own alias. */
export type SensorVisibilityMode = 'live' | 'capture' | 'passive';

/**
 * Whether a reading exists at all. `capture` is the only mode that gates one behind a gesture — its
 * whole definition is a button the student has to press. In `live` the sensor is continuously
 * reading, and in `passive` the device reads automatically at submit; in both the reading exists
 * whether or not anyone touched anything, and `passive` differs from `live` only in that the student
 * is never shown it.
 *
 * Keep this distinct from "should the evaluation be showing" (each block's own `evaluatedFor`).
 * Conflating the two is what previously left Capture on Submit unevaluable in continuous mode: it has
 * no gesture, so gating its *reading* on a submit flag meant nothing could ever produce one.
 */
export function sensorHasReading(mode: SensorVisibilityMode, captured: boolean): boolean {
  return mode === 'capture' ? captured : true;
}

/**
 * A deterministic 0..1 draw from a (nonce, salt) pair.
 *
 * Scenario values are randomised WITHIN their category so that re-picking a preset tests a different
 * setup — "just inside tolerance" at 20% of the radius and at 90% are meaningfully different tests,
 * and a fixed multiple only ever proves the one case. But it cannot be `Math.random()`: these
 * functions run during render, so a fresh draw every render would change the reading on every
 * keystroke in the editor and make the panel unusable. Instead each block holds a nonce that it bumps
 * only when the teacher picks a scenario, and the draw is a pure function of that — stable across
 * re-renders, new on every pick (including re-picking the same preset, which is how you ask for
 * another value).
 *
 * `salt` distinguishes independent draws within one scenario (distance vs bearing vs accuracy) so they
 * don't move in lockstep.
 */
export function sensorSimRandom(nonce: number, salt: number): number {
  let h = (Math.imul(nonce, 2654435761) + Math.imul(salt, 40503)) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 2246822507) >>> 0;
  h ^= h >>> 13; h = Math.imul(h, 3266489909) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** One typed component of a manual reading — a heading for Compass, a latitude and a longitude for
 * GPS. Separate boxes rather than one free-text field: a coordinate pair typed into a single box has
 * to be parsed and therefore needs an explicit apply step, whereas one box per number applies as soon
 * as the numbers are valid, which is what lets the apply button go away entirely. */
export interface SensorManualField {
  key: string;
  /** No visible per-field label — the row is too narrow on a phone — so this carries the name. */
  ariaLabel: string;
  /**
   * Ghost text. Omitted where the box takes a bare number: Compass's `135` and Angle's `45` read as
   * values already entered rather than as hints, which is exactly the wrong impression for a control
   * whose whole job is to say what the reading will be. GPS keeps its ghosts because "Latitude" and
   * "Longitude" are words, and no one mistakes a word for a coordinate.
   */
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  /** The armed fault leaves nothing for this box to hold — No Reading reports nothing at all. */
  disabled?: boolean;
  /** Non-blank text that doesn't parse. Drives the highlight only; there is no message. */
  invalid?: boolean;
  /** Set when the armed scenario has no value to type into. See `SensorScenarioOption.suppressesValue`. */
  /** A permanently visible unit pinned inside the box (Compass's `°`). Ghosted and always shown,
   * rather than appended to the placeholder, so the unit survives once the teacher starts typing —
   * a placeholder disappears at exactly the moment there is a number to put a unit on. Per-field
   * because it is not universal: GPS's latitude and longitude are unitless decimals. Decorative,
   * so it is aria-hidden; state the unit in `ariaLabel` instead. */
  suffix?: string;
}

/** Which source the block is reading from. A hard either/or, not a preset among presets: in
 * `device` every simulated setting is ignored, and in `simulate` the real sensor is. Formerly the
 * device was one more entry in the scenario dropdown, which made "am I testing the sensor or a
 * fixture?" a question you had to answer by reading the selected option. */
export type SensorSourceMode = 'device' | 'simulate';

/**
 * Keeps a manual field to the characters a signed decimal can be made of.
 *
 * Applied on the way in rather than only validated afterwards: these boxes drive the readout live,
 * so a stray letter should never have been able to reach the value in the first place. Both the
 * period and the comma survive because the block parsers accept either — a pt-BR teacher types
 * `40,712` and means the same number an en-US one writes `40.712`.
 *
 * Deliberately a filter, not a `type='number'`: a number input hides typed-but-invalid text behind
 * an empty `value`, which would make the box look cleared while the readout kept the old reading.
 */
/**
 * The teacher-only "Simulate" strip that sits above a sensor block's real student widget.
 *
 * One row, no heading: `Device (switch) Simulate` and then the controls the switch governs — a
 * scenario dropdown and the manual fields.
 *
 * The switch is deliberately the same widget as the test panel's Continuous toggle, sharing its
 * markup and classes. Both answer "which of two ways is this panel behaving", and one idea should not
 * need two controls to express. The strip carries no title of its own any more: with the switch
 * naming both states inline, a "Simulate" header above a row that might be reading the real device
 * was actively contradicting the control beneath it.
 *
 * The switch is the load-bearing part. Device and Simulate are mutually exclusive sources, and making
 * the device just another entry in the scenario dropdown meant the most important question about a
 * test ("is this the real sensor?") was answered by reading one option out of eight. It also left no
 * room for the device to explain itself: on a desktop, or over plain HTTP, the honest answer is that
 * there is no sensor, and that belongs on the hint line rather than in a silent fallback.
 *
 * What each block puts in the dropdown follows from the same test. GPS keeps its full preset list,
 * because a teacher who wants a point 10 m off target cannot type that — it is a coordinate pair
 * they would have to derive. Compass and Angle keep only the SENSOR FAULTS, because a heading or an
 * angle is a single number the teacher can simply type, and a preset that only supplies a typeable
 * number is a menu entry earning nothing.
 *
 * The controls used to be wrapping pill buttons on desktop so the whole test plan was visible at
 * once, but once the list reached seven presets that took three wrapped rows in a tablet-width card
 * and crowded out the student widget underneath — which is the thing actually being tested. A
 * dropdown also means the phone and desktop panels are the same panel, rather than two layouts to
 * keep in step.
 *
 * A consequence worth knowing: a `<select>` does NOT fire `onChange` when you re-pick the option
 * that's already selected, so re-picking can't be the gesture that redraws a randomised scenario
 * value. Redrawing lives on the test bar's own Reroll button instead — the same control that rerolls
 * variables in MCQ/Dropdown/Keyword/Ordering/FITB, so it means one thing everywhere.
 *
 * The manual fields are the escape hatch a fixed menu can't cover: one specific coordinate or heading
 * the teacher already has in mind. They apply as they're typed — no apply button — and unusable text
 * gets a field highlight and nothing else, since this is a scratch input that never reaches the saved
 * block.
 */
export interface SensorScenarioOption {
  id: string;
  label: string;
  /**
   * The scenario produces no reading at all, so there is nothing for the manual boxes to hold. They
   * are disabled rather than hidden: hiding them would reflow the strip every time the teacher
   * stepped through the fault list, and a box that vanishes reads as a bug rather than as a
   * consequence of the choice just made.
   */
  suppressesValue?: boolean;
}

/**
 * Device or Simulate, as one switch — the shape both simulators share.
 *
 * Extracted so the consolidated Preview panel and the per-block strips cannot drift apart: a
 * teacher meets this control in both places and it has to look and behave identically.
 *
 * NOT the test bar's Continuous colouring, though it borrows the shape. Continuous is a true on/off,
 * where unlit means the feature is not happening; this is a two-way selector where both ends are
 * live states, so carrying that colouring over made Device read as "switched off", as if choosing
 * the real sensor had disabled something. The track and thumb stay lit in both positions and the
 * ACTIVE side is carried by the labels; the thumb's position still says which way it points.
 */
export function SensorSourceSwitch({ mode, onModeChange, deviceLabel, simulateLabel }: {
  mode: SensorSourceMode;
  /** Switching to `device` is deliberately a user gesture, which is also what iOS requires before
   *  it will grant motion or orientation permission. */
  onModeChange: (next: SensorSourceMode) => void;
  deviceLabel: string;
  simulateLabel: string;
}) {
  const simulating = mode === 'simulate';
  /** The two words either side. `aria-hidden`, because read on their own they are stray text with
   *  no stated relationship to the switch — which is why the switch itself names both positions. */
  const sideLabel = (text: string, active: boolean) => (
    <span
      aria-hidden='true'
      className={`text-xs transition-colors ${
        active ? 'font-medium text-ws-primary dark:text-ws-text-link' : 'text-ws-muted'
      }`}
    >
      {text}
    </span>
  );
  return (
    <>
      {sideLabel(deviceLabel, !simulating)}
      {/* The shared primitive. This copy had drifted: its track stayed primary-tinted in both
          states, so only the thumb moved, while the two other hand-rolled switches swapped the
          track as well. The majority spelling is now the only one. */}
      <Switch
        on={simulating}
        onChange={next => onModeChange(next ? 'simulate' : 'device')}
        label={`${deviceLabel} / ${simulateLabel}`}
      />
      {sideLabel(simulateLabel, simulating)}
    </>
  );
}

export function SensorSimulateBar({
  groupLabel, scenarios, activeScenarioId, onPickScenario, manual, mode, onModeChange, deviceTabLabel, simulateTabLabel, hint,
}: {
  /** Names the scenario dropdown for screen readers. The strip has no visible heading. */
  groupLabel: string;
  scenarios: SensorScenarioOption[];
  /** `null` when the manual fields are what's armed, so no scenario reads as selected. */
  activeScenarioId: string | null;
  onPickScenario: (id: string) => void;
  /** Set when the block isn't configured enough to derive a reading from any scenario. */
  manual: {
    label: string;
    fields: SensorManualField[];
    /** The manual reading is the armed one. */
    active: boolean;
  };
  mode: SensorSourceMode;
  /** Switching to `device` is deliberately a user gesture, which is also what iOS requires before it
   * will grant motion or orientation permission. */
  onModeChange: (next: SensorSourceMode) => void;
  deviceTabLabel: string;
  simulateTabLabel: string;
  /** In `simulate`, what the armed selection would report. In `device`, what the sensor is doing —
   * including why it cannot be read on this machine, which is the whole reason the device tab stays
   * selectable on a desktop rather than being hidden. */
  hint: string;
}) {
  const selectId = useId();

  // Separates the source switch from the controls it governs, and only while the two share a line —
  // once they stack on a phone the row break already says it, and a stray rule under the switch would
  // read as a divider between the strip and whatever follows.
  const divider = <span aria-hidden='true' className='mx-0.5 h-5 w-px shrink-0 bg-ws-border max-sm:hidden' />;

  // Deliberately narrow and fixed in BOTH layouts. They sit inline with the scenario controls, so a
  // flexing box would steal width from the dropdown on a phone and push the presets onto another row
  // on desktop. 5rem at text-xs is the floor that still shows the full "Longitude" ghost text AND a
  // signed coordinate ("-74.00601") without either being cut off; it matches the pill buttons' own
  // type size so the row reads as one strip of controls.
  const fieldInputs = manual.fields.map(f => {
    // The suffix was an absolutely-positioned sibling with a matching pr-* on the input; NumberField
    // renders it inside the same bordered box, so the wrapper and the padding dance both go.
    return (
      <NumberField
        key={f.key}
        grammar='decimal'
        allowNegative
        value={f.value}
        onChange={f.onChange}
        hasError={f.invalid}
        active={manual.active}
        disabled={f.disabled || mode === 'device'}
        suffix={f.suffix}
        ariaLabel={f.ariaLabel}
        placeholder={f.placeholder}
        className='w-24 shrink-0' />
    );
  });

  const simulating = mode === 'simulate';

  return (
    <div className='flex flex-col gap-2 rounded-lg border border-dashed border-ws-border bg-ws-hover px-3 py-2.5'>
      {/* Two groups rather than one wrapping row. Note the direction of the variants: the wide,
          single-line layout is the BASE and phone portrait is expressed with `max-sm:`. Written the
          other way round (`w-full sm:w-auto`) the override lost to the base utility in this build,
          which pinned the switch group to the full row width, starved the controls group to 0px, and
          shot the manual inputs off the right edge of the card. Measured, not guessed.
          In phone portrait the furniture (both labels, the switch, the divider, the gaps and one
          80px box) leaves the dropdown around 60px, and "Poor Position Accuracy" wants nearer 190 —
          GPS, with two coordinate boxes, overflows before the dropdown gets anything at all. The row
          was therefore already wrapping; it just wrapped wherever it ran out, which could strand an
          input beside a crushed dropdown.
          Breaking it deliberately puts the fold on a real seam: the switch chooses the source, and
          everything after it is what that choice governs. Cheap to spend a row on, too, since this
          strip is teacher-only and never renders for a student. */}
      <div className='flex flex-wrap items-center gap-1.5 sm:flex-nowrap'>
        {/* Centred only while it owns the row. Left-aligned the moment it shares one, where it is the
            first thing in a left-to-right strip and centring would float it away from the controls it
            governs. The break is the `sm` line (640px): every phone portrait is under 480px and stacks,
            while phone landscape starts around 667px and tablet portrait around 744px, so both keep
            the single row. */}
        <div className='flex shrink-0 items-center gap-1.5 max-sm:w-full max-sm:justify-center'>
          <SensorSourceSwitch
            mode={mode}
            onModeChange={onModeChange}
            deviceLabel={deviceTabLabel}
            simulateLabel={simulateTabLabel}
          />
          {divider}
        </div>
        {/* Wraps internally in phone portrait only, where GPS's two boxes drop below the dropdown
            rather than squeezing it — that is what the select's min-width forces once the three cannot
            share a line. Above the break the group is nowrap and the select gives up its floor
            instead, because the strip's container is narrower than the viewport (a block card in a
            desktop editor panel is a few hundred pixels), so leaving the wrap on would let a GPS row
            fold on a wide screen. The select absorbs the squeeze; the boxes are shrink-0 and keep
            their width. */}
        <div className='flex min-w-0 flex-1 items-center gap-1.5 max-sm:w-full max-sm:flex-wrap'>
        <label htmlFor={selectId} className='sr-only'>{groupLabel}</label>
        {/* '' is the armed-manual state, and it is SELECTABLE. It used to be a disabled
            placeholder on the reasoning that typing is what arms the manual value — but once the
            dropdown holds only faults, "none of these" is the ordinary case rather than the
            leftover one, and there has to be a way back to it after picking a fault.

            Greyed rather than unmounted in device mode: hiding the options would collapse the row
            every time the switch moved, and a control that vanishes reads as a bug where a dead one
            plainly reads as "this is not what the panel is doing right now". */}
        <SelectMenu
          id={selectId}
          ariaLabel={groupLabel}
          fullWidth
          value={activeScenarioId ?? ''}
          onChange={onPickScenario}
          options={[
            { value: '', label: manual.label },
            ...scenarios.map(sc => ({ value: sc.id, label: sc.label })),
          ]}
          disabled={!simulating}
          className='min-w-0 flex-1' />
        {fieldInputs}
        </div>
      </div>

      <p className='text-xs italic text-ws-muted'>{hint}</p>
    </div>
  );
}
